const DELAY_MS = 1000;
const LOCATION_COOKIE = 'locationPickerId';
const POSTCODE_COOKIE = 'locationPickerPostCode';
const POPUP_CLOSED_COOKIE = 'locationPickerPopupClosed';
const LOCATION_COOKIE_DAYS = 3;
const POPUP_CLOSED_COOKIE_DAYS = 1;
const WEEKDAYS = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const FALLBACK_TIME_ZONE = 'Australia/Sydney';
const SUGGEST_DEBOUNCE_MS = 250;
const SUGGEST_MIN_LENGTH = 2;
const POSTCODE_PATTERN = /^\d{4}$/;

function placeText(value) {
  if (typeof value === 'string') return value;
  return value?.text || '';
}

function latLngLiteral(location) {
  if (!location) return null;
  const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
  const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

const LOCALITY_TYPES = ['sublocality_level_1', 'sublocality', 'locality', 'colloquial_area'];
const AU_STATE_NAMES = {
  'NEW SOUTH WALES': 'NSW',
  VICTORIA: 'VIC',
  QUEENSLAND: 'QLD',
  'SOUTH AUSTRALIA': 'SA',
  'WESTERN AUSTRALIA': 'WA',
  TASMANIA: 'TAS',
  'NORTHERN TERRITORY': 'NT',
  'AUSTRALIAN CAPITAL TERRITORY': 'ACT',
};

function componentText(component) {
  return String(component?.longText || component?.shortText || '').trim();
}

function stateCode(value) {
  const text = String(value || '').trim().toUpperCase();
  if (!text) return '';
  if (AU_STATE_NAMES[text]) return AU_STATE_NAMES[text];
  const abbrev = text.match(/\b(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)\b/);
  if (abbrev) return abbrev[1];
  const name = Object.keys(AU_STATE_NAMES).sort((left, right) => right.length - left.length).find((item) => text.includes(item));
  return name ? AU_STATE_NAMES[name] : '';
}

function localityFromComponents(components) {
  if (!Array.isArray(components)) return '';
  for (const type of LOCALITY_TYPES) {
    const name = componentText(components.find((item) => item.types?.includes(type)));
    if (name) return name;
  }
  return '';
}

function stateFromComponents(components) {
  if (!Array.isArray(components)) return '';
  const component = components.find((item) => item.types?.includes('administrative_area_level_1'));
  return stateCode(component?.shortText) || stateCode(component?.longText);
}

function withState(name, state) {
  const placeName = String(name || '').trim();
  const code = stateCode(state);
  if (!placeName) return '';
  if (!code || placeName.toUpperCase().endsWith(` ${code}`)) return placeName;
  return `${placeName} ${code}`;
}

function shortPlaceName(value, postcode) {
  const code = String(postcode || '').trim();
  let name = String(value || '').trim().split(',')[0].trim();
  if (!name) return '';
  if (code) name = name.replace(new RegExp(`\\b${code}\\b`), ' ').replace(/\s+/g, ' ').trim();
  if (!name || name === code) return '';
  return name;
}

function placeRecord(name, postcode) {
  const code = String(postcode || '').trim();
  return {
    name: shortPlaceName(name, code),
    postcode: code,
  };
}

function locationLabel(name, postcode) {
  const place = placeRecord(name, postcode);
  if (place.name && place.postcode) return `${place.name} - ${place.postcode}`;
  return place.postcode || place.name;
}

function predictionName(prediction, place, postcode) {
  const components = place?.addressComponents;
  const state = stateFromComponents(components) || stateCode(placeText(prediction?.secondaryText)) || stateCode(placeText(place?.displayName));
  const locality = localityFromComponents(components);
  if (locality) return withState(locality, state);
  const candidates = [
    placeText(prediction?.mainText),
    placeText(place?.displayName),
    placeText(prediction?.text),
    placeText(prediction?.secondaryText).split(',')[0],
  ];
  return withState(candidates.map((value) => shortPlaceName(value, postcode)).find(Boolean) || '', state);
}

function splitPlaceName(name) {
  const text = String(name || '').trim();
  const match = text.match(/^(.*)\s+(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)$/i);
  if (!match) return { suburb: text, state: '' };
  return { suburb: match[1].trim(), state: match[2].toUpperCase() };
}

function loadPlacesLibrary(apiKey) {
  if (window.google?.maps?.importLibrary) return window.google.maps.importLibrary('places');
  if (!loadPlacesLibrary.promise) {
    loadPlacesLibrary.promise = new Promise((resolve, reject) => {
      const params = { key: apiKey, v: 'weekly' };
      ((g) => {
        let loader;
        let script;
        const googleNamespace = (window.google = window.google || {});
        const maps = (googleNamespace.maps = googleNamespace.maps || {});
        const libraries = new Set();
        const search = new URLSearchParams();
        const load = () =>
          loader ||
          (loader = new Promise((done, fail) => {
            let settled = false;
            const finish = (callback) => {
              if (settled) return;
              settled = true;
              callback();
            };
            script = document.createElement('script');
            search.set('libraries', [...libraries].join(','));
            Object.entries(g).forEach(([key, value]) => {
              search.set(key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`), value);
            });
            search.set('callback', 'google.maps.__ib__');
            script.src = `https://maps.googleapis.com/maps/api/js?${search}`;
            script.async = true;
            maps.__ib__ = () => finish(done);
            script.onerror = () => {
              loader = null;
              finish(() => fail(new Error('Google Maps could not load.')));
            };
            window.setTimeout(() => {
              if (settled) return;
              loader = null;
              finish(() => fail(new Error('Google Maps could not load.')));
            }, 8000);
            const nonce = document.querySelector('script[nonce]')?.nonce;
            if (nonce) script.nonce = nonce;
            document.head.append(script);
          }));
        if (maps.importLibrary) {
          resolve(maps.importLibrary('places'));
          return;
        }
        maps.importLibrary = (library, ...args) => libraries.add(library) && load().then(() => maps.importLibrary(library, ...args));
        maps.importLibrary('places').then(resolve, reject);
      })(params);
    }).catch((error) => {
      loadPlacesLibrary.promise = null;
      throw error;
    });
  }
  return loadPlacesLibrary.promise;
}

class LocationPicker extends HTMLElement {
  constructor() {
    super();
    this.locationSettled = false;
    this.locationPopupOpen = false;
    this.mapsKey = '';
    this.suggestStates = new WeakMap();
    this.suggestInputs = new Set();
    this.onCollectionAnchorSlugChanged = (event) => {
      const anchor = event.currentTarget;
      const href = anchor.getAttribute('href');
      if (!href || !href.includes('collections')) return;
      const { oldSlug, newSlug, replacements } = event.detail || {};
      const next = (replacements && replacements[href]) || this.rewriteLocationHref(href, oldSlug, newSlug);
      if (next === href) return;
      anchor.setAttribute('href', next);
    };
  }

  connectedCallback() {
    this.suggestAbort?.abort();
    this.suggestAbort = new AbortController();
    this.suggestInputs = new Set();
    this.mapsKey = this.dataset.mapsKey || '';
    const savedPlace = this.readSavedPlace();
    if (savedPlace?.postcode) {
      this.fillPostcodeInputs(savedPlace.postcode);
      this.showPostcodeResult(savedPlace);
    }

    const cookieLocationId = this.readLocationCookie();
    if (cookieLocationId && savedPlace?.postcode) {
      this.rewriteLocationLinks(cookieLocationId);
      this.markLocationAvailable();
      this.applyPostcodeCheck(this.querySelector('[data-location-picker-form]'), savedPlace.postcode, savedPlace.name);
    }

    this.onSectionLoad = (event) => {
      const dialog = this.dialogForSection(event.detail.sectionId);
      if (!dialog?.hasAttribute('data-location-picker-popup')) return;
      this.schedulePopup(dialog);
      if (window.Shopify?.designMode) this.openPopup(dialog);
    };

    this.onSectionUnload = (event) => {
      const dialog = this.dialogForSection(event.detail.sectionId);
      if (dialog?.dataset.locationPickerReady === 'true' && !dialog.closest('.shopify-section')) {
        dialog.remove();
      }
    };

    this.onSectionSelect = (event) => {
      const dialog = this.dialogForSection(event.detail.sectionId);
      if (dialog?.hasAttribute('data-location-picker-popup')) this.openPopup(dialog);
    };

    this.onSectionDeselect = (event) => {
      const dialog = this.dialogForSection(event.detail.sectionId);
      if (dialog?.hasAttribute('open') && typeof dialog.hide === 'function') dialog.hide();
    };

    this.onModalClosed = () => {
      if (!this.locationPopupOpen) return;
      queueMicrotask(() => {
        if (!this.locationPopupOpen) return;
        const stillOpen = Array.from(this.locationPickerDialogs()).some((dialog) => dialog.hasAttribute('open'));
        if (stillOpen) return;
        this.locationPopupOpen = false;
        this.rememberPopupClosed();
      });
    };

    this.onFormSubmit = (event) => {
      const form = event.target?.closest?.('[data-location-picker-form]');
      if (!form) return;
      event.preventDefault();
      const input = form.querySelector('input.location-picker__postcode');
      const postcode = input?.value?.trim();
      if (!postcode) return;
      if (POSTCODE_PATTERN.test(postcode)) {
        this.hideSuggestions(input);
        this.applyPostcodeCheck(form, postcode);
        return;
      }
      if (input && this.isSuggestOpen(input)) this.selectActiveSuggestion(input);
    };

    this.onSuggestFocusIn = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.classList.contains('location-picker__postcode')) return;
      this.bindSuggestInput(input, { load: true });
    };

    this.onFormReset = (event) => {
      const form = event.target;
      if (!form?.matches?.('[data-location-picker-form]')) return;
      this.postcodeCheckId = (this.postcodeCheckId || 0) + 1;
      this.showPostcodeForm();
      form.querySelector('input.location-picker__postcode').focus();
    };

    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('shopify:section:unload', this.onSectionUnload);
    document.addEventListener('shopify:section:select', this.onSectionSelect);
    document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
    document.body.addEventListener('modalClosed', this.onModalClosed);
    document.addEventListener('submit', this.onFormSubmit);
    document.addEventListener('reset', this.onFormReset);
    document.addEventListener('focusin', this.onSuggestFocusIn, { signal: this.suggestAbort.signal });

    this.init();
  }

  disconnectedCallback() {
    document.querySelectorAll('a').forEach((anchor) => {
      anchor.removeEventListener('location:slug-changed', this.onCollectionAnchorSlugChanged);
    });
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    document.removeEventListener('shopify:section:unload', this.onSectionUnload);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    document.body.removeEventListener('modalClosed', this.onModalClosed);
    document.removeEventListener('submit', this.onFormSubmit);
    document.removeEventListener('reset', this.onFormReset);
    this.stopBirdWidgetWatch();
    this.suggestAbort?.abort();
    this.suggestInputs.forEach((input) => {
      const state = this.suggestStates.get(input);
      if (state?.timer) window.clearTimeout(state.timer);
    });
  }

  locationPickerDialogs() {
    return document.querySelectorAll('modal-dialog[data-location-picker-popup]');
  }

  postcodeInputs() {
    return document.querySelectorAll('input.location-picker__postcode');
  }

  locationPickerForms() {
    return document.querySelectorAll('[data-location-picker-form]');
  }

  clearCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax`;
  }

  showPostcodeResult(placeOrPostcode, name = '') {
    const place = this.asPlace(placeOrPostcode, name);
    if (!place.postcode && !place.name) return;
    const value = locationLabel(place.name, place.postcode);
    this.locationPickerForms().forEach((form) => {
      const result = form.querySelector('.location-picker__result');
      const initial = form.querySelector('.location-picker__initial');
      const label = form.querySelector('[data-location-picker-postcode]');
      if (label) label.textContent = value;
      if (result) result.hidden = false;
      if (initial) initial.hidden = true;
    });
  }

  showPostcodeForm() {
    this.locationPickerForms().forEach((form) => {
      const result = form.querySelector('.location-picker__result');
      const initial = form.querySelector('.location-picker__initial');
      const label = form.querySelector('[data-location-picker-postcode]');
      const input = form.querySelector('input.location-picker__postcode');
      if (label) label.textContent = '';
      if (input) {
        input.value = '';
        this.resetSuggestSession(input);
      }
      if (result) result.hidden = true;
      if (initial) initial.hidden = false;
    });
  }

  fillPostcodeInputs(postcode, { overwrite = false } = {}) {
    if (postcode == null || postcode === '') return;
    const value = String(postcode);
    this.postcodeInputs().forEach((input) => {
      if (overwrite || input.value === '') input.value = value;
    });
  }

  setStatus(nodes, message) {
    nodes.forEach((node) => {
      node.hidden = false;
      node.innerHTML = message;
    });
  }

  readCookie(name) {
    const prefix = `${name}=`;
    const parts = document.cookie.split(';');
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim();
      if (part.indexOf(prefix) === 0) return decodeURIComponent(part.substring(prefix.length));
    }
    return null;
  }

  writeCookie(name, value, days = LOCATION_COOKIE_DAYS) {
    if (value == null || value === '') return;
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  }

  readLocationCookie() {
    return this.readCookie(LOCATION_COOKIE);
  }

  asPlace(placeOrPostcode, name = '') {
    if (placeOrPostcode && typeof placeOrPostcode === 'object') return placeRecord(placeOrPostcode.name, placeOrPostcode.postcode);
    return placeRecord(name, placeOrPostcode);
  }

  readSavedPlace() {
    const raw = this.readCookie(POSTCODE_COOKIE);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && parsed.postcode != null && String(parsed.postcode).trim() !== '') {
        return placeRecord(parsed.name, parsed.postcode);
      }
    } catch {
      // Older cookies store the postcode on its own.
    }
    const postcode = String(raw).trim();
    if (!postcode) return null;
    return placeRecord('', postcode);
  }

  writeSavedPlace(placeOrPostcode, name = '') {
    const place = this.asPlace(placeOrPostcode, name);
    if (!place.postcode) return;
    this.writeCookie(POSTCODE_COOKIE, JSON.stringify(place));
  }

  isProductPage() {
    return window.pageType === 'product';
  }

  markLocationAvailable() {
    this.locationPickerDialogs().forEach((dialog) => {
      dialog.setAttribute('data-location-available', '');
    });
  }

  closePopup() {
    this.locationPickerDialogs().forEach((dialog) => {
      if (dialog.hasAttribute('open') && typeof dialog.hide === 'function') dialog.hide();
    });
  }

  locationById(locationId) {
    if (locationId == null || locationId === '') return null;
    const id = Number(locationId);
    if (!Number.isFinite(id)) return null;
    const locations = Array.isArray(window.storeLocations) ? window.storeLocations : [];
    return locations.find((location) => Number(location.id) === id) || null;
  }

  rewriteLocationHref(href, oldSlug, newSlug) {
    if (!href || !oldSlug || !newSlug || oldSlug === newSlug) return href;

    const suffix = `-${oldSlug}`;
    try {
      const url = new URL(href, window.location.origin);
      const segments = url.pathname.split('/');
      const lastIndex = segments.length - 1;
      const last = segments[lastIndex] || '';
      if (!last.endsWith(suffix) || last.length <= suffix.length) return href;
      segments[lastIndex] = `${last.slice(0, -suffix.length)}-${newSlug}`;
      url.pathname = segments.join('/');
      if (/^https?:\/\//i.test(href)) return url.toString();
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return href;
    }
  }

  publishLocationSlugChange(oldSlug, newSlug, replacements = {}) {
    const replacementCount = replacements ? Object.keys(replacements).length : 0;
    if ((!oldSlug || !newSlug || oldSlug === newSlug) && replacementCount === 0) return;
    const detail = { oldSlug, newSlug, replacements };
    document.dispatchEvent(new CustomEvent('location:slug-changed', { detail }));
    document.querySelectorAll('a').forEach((anchor) => {
      anchor.addEventListener('location:slug-changed', this.onCollectionAnchorSlugChanged);
      anchor.dispatchEvent(new CustomEvent('location:slug-changed', { detail }));
    });
  }

  rewriteLocationLinks(locationId) {
    const nextLocation = this.locationById(locationId);
    if (!nextLocation?.slug) return;

    const oldSlugs = new Set();
    const replacements = new Map();

    document.querySelectorAll('[data-location-id]').forEach((el) => {
      const currentId = el.dataset.locationId;
      if (currentId == null || currentId === '') return;
      if (Number(currentId) === Number(nextLocation.id)) return;

      if (!el.hasAttribute('href')) return;

      const currentHref = el.getAttribute('href');
      let nextHref = currentHref;
      if (currentId == 'parent') {
        nextHref = currentHref.concat('-', nextLocation.slug);
      } else {
        const currentLocation = this.locationById(currentId);
        if (!currentLocation?.slug) return;

        oldSlugs.add(currentLocation.slug);
        nextHref = this.rewriteLocationHref(currentHref, currentLocation.slug, nextLocation.slug);
      }

      if (currentHref && nextHref && currentHref !== nextHref) {
        replacements.set(currentHref, nextHref);
        el.setAttribute('href', nextHref);
      }

      el.dataset.locationId = String(nextLocation.id);
    });

    const replacementMap = Object.fromEntries(replacements);
    if (oldSlugs.size === 0) {
      this.publishLocationSlugChange(nextLocation.slug, nextLocation.slug, replacementMap);
      return;
    }
    oldSlugs.forEach((oldSlug) => this.publishLocationSlugChange(oldSlug, nextLocation.slug, replacementMap));
  }

  zoneMatchesPlace(zone, name, postcode) {
    const zipCodes = zone?.zipCodes;
    if (!Array.isArray(zipCodes)) return false;
    const code = String(postcode || '').trim();
    const suburb = splitPlaceName(name).suburb.trim().toUpperCase();
    if (!suburb || !POSTCODE_PATTERN.test(code)) return false;
    const suburbListed = zipCodes.some((entry) => String(entry).trim().toUpperCase() === suburb);
    const postcodeListed = zipCodes.includes(Number(code)) || zipCodes.includes(code);
    return suburbListed && postcodeListed;
  }

  locationForPostcode(postcode, name = '') {
    const config = window.BirdApp?.sdk?.config;
    const locations = Array.isArray(config?.locations) ? config.locations : [];
    for (let i = 0; i < locations.length; i++) {
      const location = locations[i];
      if (!location?.delivery) continue;
      if (!this.zoneMatchesPlace(location.delivery.zone, name, postcode)) continue;
      if (location.id == null || location.id === '') continue;
      return location;
    }
    return null;
  }

  parseClock(value) {
    if (typeof value !== 'string') return null;
    const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const seconds = match[3] == null ? 0 : Number(match[3]);
    if (hours > 23 || minutes > 59 || seconds > 59) return null;
    return hours * 3600 + minutes * 60 + seconds;
  }

  nowInTimeZone(timeZone) {
    const zone = timeZone || FALLBACK_TIME_ZONE;
    let formatter;
    try {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      });
    } catch {
      if (zone === FALLBACK_TIME_ZONE) return { day: 0, seconds: 0 };
      return this.nowInTimeZone(FALLBACK_TIME_ZONE);
    }

    const parts = {};
    formatter.formatToParts(new Date()).forEach((part) => {
      if (part.type !== 'literal') parts[part.type] = part.value;
    });
    let hour = Number(parts.hour);
    if (hour === 24) hour = 0;
    return {
      day: WEEKDAYS[parts.weekday],
      seconds: hour * 3600 + Number(parts.minute) * 60 + Number(parts.second),
    };
  }

  earliestDeliveryMessage(location) {
    const config = window.BirdApp?.sdk?.config;
    const now = this.nowInTimeZone(location?.timezone || config?.settings?.timezone);
    const slots = location?.delivery?.weekdays?.[now.day]?.slots;
    const open = Array.isArray(slots) && slots.some((slot) => {
      const start = this.parseClock(slot?.startTime);
      return start != null && now.seconds <= start;
    });
    return open ? 'Earliest delivery available: <strong>today</strong>' : 'Earliest delivery available: <strong>tomorrow</strong>';
  }

  async applyPostcodeCheck(form, postcode, name = '') {
    const checkId = (this.postcodeCheckId = (this.postcodeCheckId || 0) + 1);
    const code = String(postcode || '').trim();
    if (!code) return;
    if (!window.BirdApp?.sdk) await this.waitForBirdSdk();
    if (checkId !== this.postcodeCheckId) return;
    let placeName = String(name || '').trim();
    if (!placeName || placeName === code) {
      placeName = await this.placeNameForPostcode(code);
      if (checkId !== this.postcodeCheckId) return;
    }
    const place = placeRecord(placeName, code);
    const location = this.locationForPostcode(place.postcode, place.name);
    if (!location) {
      this.setStatus(document.querySelectorAll('[data-location-picker-status]'), 'Delivery not available');
      this.showPostcodeResult(place);
      return;
    }

    this.fillPostcodeInputs(code, { overwrite: true });
    this.applyLocation(location.id, place);
    this.syncBirdProductWidget(place.postcode);
    this.setStatus(document.querySelectorAll('[data-location-picker-status]'), this.earliestDeliveryMessage(location));
    this.showPostcodeResult(place);
  }

  birdSelectionPostcode() {
    const current = window.BirdApp?.sdk?.selections?.current;
    const located = current?.customerLocation;
    const postcode = current?.zip ?? current?.postcode ?? located?.zip ?? located?.postcode ?? located?.postalCode;
    if (postcode == null || postcode === '') return '';
    return String(postcode).trim();
  }

  syncBirdProductWidget(postcode) {
    const code = String(postcode || '').trim();
    if (!POSTCODE_PATTERN.test(code)) return;
    this.birdWidgetOpened = false;
    this.birdWidgetPostcode = code;
    if (this.fillBirdWidgets()) return;
    this.watchBirdProductWidget();
  }

  fillBirdWidgets() {
    const productReady = this.fillBirdProductWidget();
    const cartReady = this.fillBirdCartWidget();
    if (productReady && cartReady && this.birdWidgetCheckTimer == null) this.stopBirdWidgetWatch();
    return productReady && cartReady;
  }

  fillBirdCartWidget() {
    const code = this.birdWidgetPostcode;
    if (!POSTCODE_PATTERN.test(code || '')) return false;
    if (this.isProductPage() && this.birdWidgetCheckTimer != null) return false;
    const input = document.querySelector('bird-cart-widget input.bird-address-search__input');
    if (!(input instanceof HTMLInputElement)) return false;
    const current = input.value.trim();
    if (current === code) return true;
    if (!current && this.birdSelectionPostcode() === code) return true;
    const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
    if (setter) setter.call(input, code);
    else input.value = code;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  fillBirdProductWidget() {
    if (this.birdWidgetSyncing) return true;
    if (!this.isProductPage()) return true;
    const code = this.birdWidgetPostcode;
    if (!POSTCODE_PATTERN.test(code || '')) return false;
    if (this.birdSelectionPostcode() === code) return true;
    const root = document.querySelector('#bird-product-widget');
    if (!root) return false;
    let input = root.querySelector('input.bird-pw-input__field');
    if (!(input instanceof HTMLInputElement)) {
      if (this.birdWidgetOpened) return false;
      const change = root.querySelector('button.bird-pw-footer__action');
      if (!(change instanceof HTMLButtonElement)) return false;
      this.birdWidgetOpened = true;
      change.click();
      input = root.querySelector('input.bird-pw-input__field');
      if (!(input instanceof HTMLInputElement)) return false;
    }
    this.birdWidgetSyncing = true;
    const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
    if (setter) setter.call(input, code);
    else input.value = code;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
    const check = root.querySelector('button.bird-pw-input__cta');
    this.birdWidgetSyncing = false;
    if (!(check instanceof HTMLButtonElement)) return false;
    window.clearTimeout(this.birdWidgetCheckTimer);
    this.birdWidgetSyncing = true;
    this.birdWidgetCheckTimer = window.setTimeout(() => {
      this.birdWidgetCheckTimer = null;
      this.birdWidgetSyncing = false;
      if (this.birdWidgetPostcode !== code || !check.isConnected) {
        this.fillBirdWidgets();
        return;
      }
      this.birdWidgetSyncing = true;
      check.click();
      this.birdWidgetSyncing = false;
      this.birdWidgetOpened = false;
      this.fillBirdWidgets();
    }, 200);
    return true;
  }

  watchBirdProductWidget() {
    if (this.birdWidgetObserver) return;
    this.birdWidgetObserver = new MutationObserver(() => {
      this.fillBirdWidgets();
    });
    this.birdWidgetObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  stopBirdWidgetWatch() {
    if (this.birdWidgetCheckTimer != null) {
      window.clearTimeout(this.birdWidgetCheckTimer);
      this.birdWidgetCheckTimer = null;
      this.birdWidgetSyncing = false;
    }
    this.birdWidgetObserver?.disconnect();
    this.birdWidgetObserver = null;
  }

  applyLocation(locationId, placeOrPostcode, name = '') {
    const place = this.asPlace(placeOrPostcode, name);
    this.writeSavedPlace(place);
    if (locationId == null || locationId === '') return;
    this.writeCookie(LOCATION_COOKIE, locationId);
    this.rewriteLocationLinks(locationId);

    if (this.locationSettled) return;
    this.locationSettled = true;
    this.markLocationAvailable();
    this.closePopup();
  }

  hasNoCustomerLocation() {
    return window.BirdApp?.sdk?.selections?.current?.customerLocation == null;
  }

  dialogForSection(sectionId) {
    return document.getElementById(`LocationPickerPopup-${sectionId}`);
  }

  async waitForBirdSdk() {
    const started = Date.now();
    while (Date.now() - started < 15000) {
      if (window.BirdApp?.ready) return window.BirdApp.ready;
      await new Promise((resolve) => window.setTimeout(resolve, 200));
    }
    return null;
  }

  rememberPopupClosed() {
    this.writeCookie(POPUP_CLOSED_COOKIE, '1', POPUP_CLOSED_COOKIE_DAYS);
  }

  openPopup(dialog) {
    if (!dialog || typeof dialog.show !== 'function') return;
    if (!dialog.hasAttribute('open')) {
      dialog.hidden = false;
      dialog.show();
      this.locationPopupOpen = true;
    }
  }

  schedulePopup(dialog) {
    if (!dialog || dialog.dataset.locationPickerReady === 'true') return;
    dialog.dataset.locationPickerReady = 'true';
    if (window.Shopify?.designMode) return;
    if (this.isProductPage() || this.readLocationCookie() || this.readCookie(POPUP_CLOSED_COOKIE)) return;

    const sdkReady = this.waitForBirdSdk();
    window.setTimeout(async () => {
      const sdk = await sdkReady;
      if (!sdk || !dialog.isConnected || this.isProductPage() || this.readLocationCookie() || this.readCookie(POPUP_CLOSED_COOKIE) || !this.hasNoCustomerLocation()) return;
      this.openPopup(dialog);
    }, DELAY_MS);
  }

  suggestionState(input) {
    let state = this.suggestStates.get(input);
    if (!state) {
      state = { token: null, timer: 0, requestId: 0, suggestions: [], activeIndex: -1, selecting: false };
      this.suggestStates.set(input, state);
    }
    return state;
  }

  suggestionPanel(input) {
    return input?.closest('.location-picker__suggest')?.querySelector('.location-picker__suggestions-panel') || null;
  }

  suggestionList(input) {
    return this.suggestionPanel(input)?.querySelector('[role="listbox"]') || null;
  }

  isSuggestOpen(input) {
    const panel = this.suggestionPanel(input);
    return Boolean(panel && !panel.hidden && this.suggestionState(input).suggestions.length);
  }

  bindSuggestInputs() {
    this.postcodeInputs().forEach((input) => this.bindSuggestInput(input));
  }

  bindSuggestInput(input, { load = false } = {}) {
    if (!this.mapsKey || !input || this.suggestInputs.has(input)) {
      if (load) this.ensurePlaces();
      return;
    }
    this.suggestInputs.add(input);
    const signal = this.suggestAbort.signal;
    input.addEventListener('focus', () => this.ensurePlaces(), { signal });
    input.addEventListener('input', () => this.onSuggestInput(input), { signal });
    input.addEventListener('keydown', (event) => this.onSuggestKeydown(event), { signal });
    input.addEventListener('blur', () => {
      window.setTimeout(() => {
        const form = input.closest('[data-location-picker-form]');
        if (form?.contains(document.activeElement)) return;
        this.hideSuggestions(input);
      }, 0);
    }, { signal });
    const list = this.suggestionList(input);
    list?.addEventListener('mousedown', (event) => {
      const option = event.target.closest('[role="option"]');
      if (!option || !list.contains(option)) return;
      event.preventDefault();
      this.selectSuggestion(input, [...list.children].indexOf(option));
    }, { signal });
    if (load) this.ensurePlaces();
  }

  ensurePlaces() {
    if (!this.mapsKey) return Promise.resolve(null);
    if (!this.placesLibrary) {
      this.placesLibrary = loadPlacesLibrary(this.mapsKey).catch((error) => {
        this.placesLibrary = null;
        throw error;
      });
    }
    return this.placesLibrary.catch(() => null);
  }

  onSuggestInput(input) {
    const value = input.value.trim();
    const state = this.suggestionState(input);
    if (value.length < SUGGEST_MIN_LENGTH) {
      this.hideSuggestions(input);
      if (value === '') state.token = null;
      return;
    }
    window.clearTimeout(state.timer);
    state.timer = window.setTimeout(() => this.fetchSuggestions(input), SUGGEST_DEBOUNCE_MS);
  }

  async fetchSuggestions(input) {
    const state = this.suggestionState(input);
    const value = input.value.trim();
    if (value.length < SUGGEST_MIN_LENGTH) return;
    const places = await this.ensurePlaces();
    if (!places || !input.isConnected || input.value.trim() !== value) return;
    const { AutocompleteSuggestion, AutocompleteSessionToken } = places;
    if (!state.token) state.token = new AutocompleteSessionToken();
    const requestId = ++state.requestId;
    let suggestions = [];
    try {
      if (POSTCODE_PATTERN.test(value)) suggestions = await this.localitiesForPostcode(value);
      if (!suggestions.length) {
        const result = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: value,
          sessionToken: state.token,
          includedRegionCodes: ['au'],
          includedPrimaryTypes: ['locality', 'postal_code', 'sublocality', 'colloquial_area'],
          language: 'en-AU',
        });
        const predictions = (result?.suggestions || []).map((item) => item.placePrediction).filter(Boolean);
        suggestions = (await Promise.all(predictions.map((prediction) => this.describePrediction(prediction, places)))).flat();
      }
    } catch {
      suggestions = [];
    }
    if (requestId !== state.requestId || !input.isConnected || input.value.trim() !== value) return;
    const seen = new Set();
    state.suggestions = suggestions.filter((suggestion) => {
      if (!suggestion?.name && !suggestion?.postcode) return false;
      const key = `${suggestion.name}|${suggestion.postcode}`.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    state.activeIndex = state.suggestions.length ? 0 : -1;
    this.renderSuggestions(input);
  }

  renderSuggestions(input) {
    const state = this.suggestionState(input);
    const panel = this.suggestionPanel(input);
    const list = this.suggestionList(input);
    if (!panel || !list) return;
    list.replaceChildren();
    if (!state.suggestions.length) {
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      return;
    }
    state.suggestions.forEach((suggestion, index) => {
      const option = document.createElement('li');
      option.className = 'location-picker__suggestion';
      option.id = `${input.id}-suggestion-${index}`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', index === state.activeIndex ? 'true' : 'false');
      const main = document.createElement('span');
      main.className = 'location-picker__suggestion-main';
      main.textContent = locationLabel(suggestion.name, suggestion.postcode);
      option.append(main);
      list.append(option);
    });
    panel.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    const active = list.querySelector('[aria-selected="true"]');
    if (active) {
      input.setAttribute('aria-activedescendant', active.id);
      const top = active.offsetTop;
      const bottom = top + active.offsetHeight;
      if (top < list.scrollTop) list.scrollTop = top;
      else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
    }
  }

  hideSuggestions(input) {
    if (!input) return;
    const state = this.suggestStates.get(input);
    if (state?.timer) {
      window.clearTimeout(state.timer);
      state.timer = 0;
    }
    if (state) {
      state.requestId += 1;
      state.suggestions = [];
      state.activeIndex = -1;
    }
    const panel = this.suggestionPanel(input);
    const list = this.suggestionList(input);
    if (list) list.replaceChildren();
    if (panel) panel.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  }

  resetSuggestSession(input) {
    const state = this.suggestStates.get(input);
    if (state) state.token = null;
    this.hideSuggestions(input);
  }

  onSuggestKeydown(event) {
    const input = event.currentTarget;
    if (event.key === 'Escape') {
      if (!this.isSuggestOpen(input)) return;
      event.preventDefault();
      this.hideSuggestions(input);
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!this.isSuggestOpen(input)) return;
      event.preventDefault();
      const state = this.suggestionState(input);
      const last = state.suggestions.length - 1;
      if (event.key === 'ArrowDown') state.activeIndex = Math.min(state.activeIndex + 1, last);
      else state.activeIndex = Math.max(state.activeIndex - 1, 0);
      this.renderSuggestions(input);
      return;
    }
    if (event.key === 'Enter' && !POSTCODE_PATTERN.test(input.value.trim()) && this.isSuggestOpen(input)) {
      event.preventDefault();
      this.selectActiveSuggestion(input);
    }
  }

  selectActiveSuggestion(input) {
    const state = this.suggestionState(input);
    const index = state.activeIndex >= 0 ? state.activeIndex : 0;
    this.selectSuggestion(input, index);
  }

  async selectSuggestion(input, index) {
    const state = this.suggestionState(input);
    if (state.selecting) return;
    const suggestion = state.suggestions[index];
    const prediction = suggestion?.prediction;
    if (!suggestion) return;
    state.selecting = true;
    state.token = null;
    const form = input.closest('[data-location-picker-form]');
    this.hideSuggestions(input);
    if (!prediction) {
      if (POSTCODE_PATTERN.test(suggestion.postcode) && input.isConnected) {
        input.value = suggestion.postcode;
        this.applyPostcodeCheck(form, suggestion.postcode, suggestion.name);
      }
      state.selecting = false;
      return;
    }
    try {
      let name = suggestion.name;
      let postcode = suggestion.postcode;
      if (!POSTCODE_PATTERN.test(postcode)) {
        const codes = await this.postcodesForName(name);
        if (codes.length === 1) postcode = codes[0];
      }
      if (!POSTCODE_PATTERN.test(postcode)) {
        const place = prediction.toPlace();
        await place.fetchFields({ fields: ['addressComponents', 'postalAddress', 'location', 'displayName'] });
        postcode = this.postalCodeFromPlace(place) || this.postalCodeFromPostalAddress(place) || '';
        if (!POSTCODE_PATTERN.test(postcode)) {
          const codes = await this.postcodesForName(predictionName(prediction, place, postcode) || name);
          if (codes.length === 1) postcode = codes[0];
        }
        if (!POSTCODE_PATTERN.test(postcode)) postcode = await this.postalCodeFromNearbyLocality(place, prediction) || '';
        name = predictionName(prediction, place, postcode) || name;
      }
      if (!POSTCODE_PATTERN.test(postcode) || !input.isConnected) return;
      input.value = postcode;
      this.applyPostcodeCheck(form, postcode, name);
    } catch {
      // Place details failed. A typed postcode can still be checked.
    } finally {
      state.selecting = false;
    }
  }

  postcodeIndex() {
    if (!this.postcodesPromise) {
      const url = this.dataset.postcodesUrl;
      this.postcodesPromise = url
        ? fetch(url).then((response) => (response.ok ? response.json() : {})).catch(() => ({}))
        : Promise.resolve({});
    }
    return this.postcodesPromise;
  }

  async localitiesForPostcode(postcode) {
    const index = await this.postcodeIndex();
    const rows = index?.[postcode];
    if (!Array.isArray(rows)) return [];
    return rows.map((row) => {
      const [suburb, state] = String(row).split('|');
      return { prediction: null, name: withState(suburb, state), postcode };
    }).filter((suggestion) => suggestion.name);
  }

  async placesByName() {
    if (!this.placesByNamePromise) {
      this.placesByNamePromise = this.postcodeIndex().then((index) => {
        const places = new Map();
        Object.entries(index || {}).forEach(([postcode, rows]) => {
          if (!Array.isArray(rows) || !POSTCODE_PATTERN.test(postcode)) return;
          rows.forEach((row) => {
            const [suburb, state] = String(row).split('|');
            if (!suburb || !state) return;
            const key = `${suburb}|${state}`.toLowerCase();
            const codes = places.get(key) || [];
            if (!codes.includes(postcode)) codes.push(postcode);
            places.set(key, codes);
          });
        });
        return places;
      });
    }
    return this.placesByNamePromise;
  }

  async postcodesForName(name) {
    const { suburb, state } = splitPlaceName(name);
    if (!suburb || !state) return [];
    const places = await this.placesByName();
    return places.get(`${suburb}|${state}`.toLowerCase()) || [];
  }

  async describePrediction(prediction, places) {
    const fallbackName = predictionName(prediction);
    const { Place } = places || {};
    const fromIndex = async (name) => {
      const codes = await this.postcodesForName(name);
      if (!codes.length) return [{ prediction, name, postcode: '' }];
      return codes.map((postcode) => ({ prediction, name, postcode }));
    };
    if (!Place || !prediction?.placeId) return fromIndex(fallbackName);
    try {
      const place = new Place({ id: prediction.placeId });
      await place.fetchFields({ fields: ['addressComponents', 'postalAddress', 'location', 'displayName'] });
      let postcode = this.postalCodeFromPlace(place) || this.postalCodeFromPostalAddress(place) || '';
      const componentName = predictionName(prediction, place, postcode) || fallbackName;
      if (POSTCODE_PATTERN.test(postcode)) return [{ prediction, name: componentName, postcode }];
      const state = splitPlaceName(componentName).state || stateFromComponents(place.addressComponents);
      const mainName = withState(shortPlaceName(placeText(prediction.mainText) || placeText(prediction.text), postcode), state);
      const indexed = await fromIndex(mainName);
      if (indexed.some((item) => POSTCODE_PATTERN.test(item.postcode))) return indexed;
      const named = await fromIndex(componentName);
      if (named.some((item) => POSTCODE_PATTERN.test(item.postcode))) return named;
      postcode = await this.postalCodeFromNearbyLocality(place, prediction) || '';
      if (!POSTCODE_PATTERN.test(postcode)) postcode = '';
      return [{ prediction, name: componentName, postcode }];
    } catch {
      return fromIndex(fallbackName);
    }
  }

  async placeNameForPostcode(postcode) {
    const code = String(postcode || '').trim();
    if (!POSTCODE_PATTERN.test(code)) return '';
    const places = await this.ensurePlaces();
    const { AutocompleteSuggestion, AutocompleteSessionToken, Place } = places || {};
    if (!AutocompleteSuggestion || !AutocompleteSessionToken || !Place) return '';
    try {
      const result = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: code,
        sessionToken: new AutocompleteSessionToken(),
        includedRegionCodes: ['au'],
        includedPrimaryTypes: ['locality', 'postal_code', 'sublocality'],
        language: 'en-AU',
      });
      const predictions = (result?.suggestions || []).map((item) => item.placePrediction).filter(Boolean);
      for (const prediction of predictions.slice(0, 3)) {
        if (!prediction.placeId) continue;
        try {
          const place = new Place({ id: prediction.placeId });
          await place.fetchFields({ fields: ['addressComponents', 'postalAddress', 'displayName'] });
          const found = this.postalCodeFromPlace(place) || this.postalCodeFromPostalAddress(place);
          if (found !== code) continue;
          const name = predictionName(prediction, place, code);
          if (name) return name;
        } catch {
          continue;
        }
      }
    } catch {
      return '';
    }
    return '';
  }

  postalCodeFromPlace(place) {
    const component = place?.addressComponents?.find((item) => item.types?.includes('postal_code'));
    const postcode = component?.longText || component?.shortText;
    if (postcode == null || String(postcode).trim() === '') return null;
    return String(postcode).trim();
  }

  postalCodeFromPostalAddress(place) {
    const postcode = place?.postalAddress?.postalCode;
    if (postcode == null || String(postcode).trim() === '') return null;
    return String(postcode).trim();
  }

  async postalCodeFromNearbyLocality(place, prediction) {
    const places = await this.ensurePlaces();
    const AutocompleteSuggestion = places?.AutocompleteSuggestion;
    const AutocompleteSessionToken = places?.AutocompleteSessionToken;
    const input = placeText(place?.displayName) || placeText(prediction?.mainText) || placeText(prediction?.text);
    if (!AutocompleteSuggestion || !AutocompleteSessionToken || !input) return null;
    const request = {
      input,
      sessionToken: new AutocompleteSessionToken(),
      includedRegionCodes: ['au'],
      includedPrimaryTypes: ['locality', 'postal_code'],
      language: 'en-AU',
    };
    const center = latLngLiteral(place?.location);
    if (center) request.locationBias = { center, radius: 2000 };
    try {
      const result = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      const predictions = (result?.suggestions || []).map((item) => item.placePrediction).filter(Boolean);
      const originalId = prediction?.placeId || place?.id;
      for (const next of predictions.slice(0, 4)) {
        if (originalId && next.placeId === originalId) continue;
        const nextPlace = next.toPlace();
        await nextPlace.fetchFields({ fields: ['addressComponents', 'postalAddress'] });
        const postcode = this.postalCodeFromPlace(nextPlace) || this.postalCodeFromPostalAddress(nextPlace);
        if (POSTCODE_PATTERN.test(postcode || '')) return postcode;
      }
    } catch {
      return null;
    }
    return null;
  }

  init() {
    this.locationPickerDialogs().forEach((dialog) => this.schedulePopup(dialog));
    this.bindSuggestInputs();
    window.setTimeout(() => this.bindSuggestInputs(), 0);
  }
}

if (!customElements.get('location-picker')) {
  customElements.define('location-picker', LocationPicker);
}
